/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, OnChanges, OnInit, SimpleChanges, computed} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './audit-history.component.html';

import {ObjectTypeFinder} from '../../common/constants/object-type';
import {stringToCurrency} from '../../../components/util/iso-currency-format';
import {AdnMessageParamsService} from '../../common/filters/adn-message-params.service';
import {formatLocaleDate} from '../../common/locale/locale-date-format';
import {AppLocaleService} from '../../common/locale/app-locale.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {interpolateTemplate} from '../../common/filters/interpolate-template';
import {reportMissingTranslateKey, summarizeTranslateMessage} from '../../common/filters/missing-translate-key';
import {SignalPageState} from '../../common/controller/signal-page-state';
import {map} from 'rxjs';
import {StateService} from '@uirouter/core';
import {AuditHistoryApiService} from './audit-history-api.service';

export interface AuditHistoryHook {
  objectType?: string;
  startDate?: any;
  endDate?: any;
  chartMode?: boolean;
  triggerAuditQuery?: () => void;
}

@Component({
  standalone: false,
  selector: 'adn-audit-history',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuditHistoryComponent implements OnInit, OnChanges {
  @Input() object: any;
  @Input() hook: AuditHistoryHook;
  @Input() objectType: string;

  readonly page: SignalPageState<any, {id: string; type: string; startDate: any; endDate: any; chartMode: boolean}>;
  readonly history = () => this.page.items();
  readonly masquerade = computed(() => this.history().some((item) => !!item.masqueraderUser));
  readonly currentPage = () => this.page.currentPage();
  readonly totalCount = () => this.page.totalCount();
  readonly historyResolved = () => !this.page.loading();

  constructor(
    private readonly translate: TranslateService,
    private readonly $state: StateService,
    private readonly auditHistoryApi: AuditHistoryApiService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly adnMessageParams: AdnMessageParamsService,
    private readonly appLocale: AppLocaleService,
    destroyRef: DestroyRef
  ) {
    this.page = new SignalPageState((query, page, pageSize) => this.auditHistoryApi.get(query.id, query.type, {
      page, pageSize, startDate: query.startDate, endDate: query.endDate, chartMode: query.chartMode
    }).pipe(map((data) => ({...data, results: (data.results || []).map((item) => ({
      ...item, adjustedTime: item.timestamp, messages: (item.messages || []).map((message) => this.processMessage(message))
    }))}))), 25, destroyRef);
  }

  ngOnInit(): void {
    this.bindHook();
    this.loadIfReady();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hook) {
      this.bindHook();
    }
    if (changes.object || changes.objectType || changes.hook) {
      const previousId = changes.object?.previousValue?.id;
      if (changes.objectType || (previousId && previousId !== this.object?.id)) {
        this.page.currentPage.set(1);
      }
      this.loadIfReady();
    }
  }

  private bindHook(): void {
    if (this.hook) {
      this.hook.triggerAuditQuery = () => {
        this.auditQuery();
      };
    }
  }

  private loadIfReady(): void {
    this.auditQuery();
  }

  private auditQuery(): void {
    if (!this.object?.id) {
      this.page.clear();
      return;
    }
    this.page.load({id: this.object.id, type: this.getObjectType(),
      startDate: this.hook?.startDate, endDate: this.hook?.endDate, chartMode: this.hook?.chartMode});
  }

  private getObjectType(): string | undefined {
    return this.objectType
      || _.get(this.hook, ['objectType'])
      || _.get(this.object, ['objectType', 'type'])
      || _.get(this.object, ['objectType'])
      || _.get(ObjectTypeFinder.findObjectType(this.object), ['type'])
      || _.get(ObjectTypeFinder.findObjectFromStateName(_.get(this.$state, ['current', 'name'])), ['type']);
  }

  private processMessage(message: any): any {
    const processed = this.adnMessageParams.transform(_.cloneDeep(message), {status: 'forecast.status'});
    const interpolateParams = this.formatMessageParams(_.clone(processed.parameters));
    // ngx-translate throws if key is null/undefined/'' (audit rows can omit code).
    if (processed.code) {
      processed.uiTranslation = this.translate.instant(processed.code, interpolateParams);
      if (processed.uiTranslation === processed.code) {
        processed.uiTranslation = undefined;
      }
    } else {
      reportMissingTranslateKey({
        source: 'AuditHistoryComponent.processMessage',
        key: processed.code,
        missingField: 'code',
        interpolateParams,
        messageSummary: summarizeTranslateMessage(processed)
      });
      processed.uiTranslation = undefined;
    }

    const newParams = _.clone(interpolateParams);
    const defaultCurrency = this.LocalNetworkProfile.get('defaultCurrency');
    _.forEach(newParams, (value, key) => {
      if (_.endsWith(key.toLowerCase(), 'id')) {
        delete newParams[key];
      } else if (key.toLowerCase().indexOf('name') > -1) {
        newParams[key] = '<strong>' + value + '</strong>';
      } else {
        newParams[key] = stringToCurrency(value, defaultCurrency, this.appLocale.getLocaleId());
      }
    });
    processed.apiTranslation = interpolateTemplate(this.stripLocaleDateFilters(processed.text))(newParams);
    return processed;
  }

  private formatMessageParams(params: any): any {
    if (!params) {
      return params;
    }
    const locale = this.appLocale.getLocaleId();
    const timeZone = this.LocalNetworkProfile.get('timeZone') || 'utc';
    _.forEach(['startDate', 'endDate'], (key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        params[key] = formatLocaleDate(params[key], 'short', locale, timeZone);
      }
    });
    return params;
  }

  private stripLocaleDateFilters(text: string): string {
    if (!text) {
      return text;
    }
    return String(text).replace(/\s*\|\s*localeDate\s*:\s*["'][^"']+["']/g, '');
  }
}
